import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { Plus, X, Trash2, Edit, Printer, Send, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PatientProgressNotesTab } from "@/components/ProgressNotes";
import { logClientEvent } from "@/components/AuditLog";
import { MembershipTab } from "@/components/Membership";
import { ConsentTab } from "@/components/ConsentTab";
import { DocumentsTab } from "@/components/DocumentsTab";
import { API, formatApiError, useAuth, getInitials, calcAge, formatPhone, InfoField } from "@/lib/shared";

export const EMPTY_PATIENT = {
  first_name: '', last_name: '', email: '', phone: '', date_of_birth: '',
  address: '', city: '', state: '', zip_code: '', gender: '',
  emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
  allergies: '', current_medications: '', medical_conditions: '', past_surgeries: '',
  prior_iv_therapy: false, prior_iv_notes: '', hydration_concerns: '',
  hipaa_acknowledged: false, treatment_consent: false,
  notes: '',
};

// ==================== PATIENT CHART (FULL VIEW) ====================
export const PatientChart = ({ patient, onClose, onEdit }) => {
  const { user } = useAuth();
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('overview');
  const [newNote, setNewNote] = useState('');
  const [noteCategory, setNoteCategory] = useState('general');
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(async () => {
    if (!patient?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/patients/${patient.id}/chart`);
      setChart(res.data);
    } catch (e) {
      toast.error('Failed to load patient chart');
    } finally { setLoading(false); }
  }, [patient?.id]);

  useEffect(() => {
    if (patient?.id) { setTab('overview'); load(); }
    else { setChart(null); }
  }, [patient?.id, load]);

  const addNote = async () => {
    if (!newNote.trim()) { toast.error('Note cannot be empty'); return; }
    setSavingNote(true);
    try {
      await axios.post(`${API}/patients/${patient.id}/chart-notes`, { body: newNote.trim(), category: noteCategory });
      toast.success('Note added to chart');
      setNewNote('');
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSavingNote(false); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this chart note?')) return;
    try {
      await axios.delete(`${API}/chart-notes/${noteId}`);
      toast.success('Note removed');
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const printChart = () => {
    if (!chart) { toast.error('Chart still loading'); return; }
    const p = chart.patient || patient;
    const age = calcAge(p.date_of_birth);
    const visits = chart.visits || [];
    const appts = chart.appointments || [];
    const fups = chart.follow_ups || [];
    const cnotes = chart.chart_notes || [];
    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const safe = (v, fb = '—') => (v === null || v === undefined || v === '' ? fb : esc(v));
    const today = format(new Date(), 'MMM d, yyyy h:mm a');

    const visitRows = visits.length === 0
      ? '<tr><td colspan="4" class="empty">No documented visits</td></tr>'
      : visits.slice(0, 20).map(v => `
          <tr>
            <td>${esc(v.visit_date || '')}</td>
            <td>${safe(v.service_type?.name)}</td>
            <td>
              ${v.blood_pressure ? `BP ${esc(v.blood_pressure)} ` : ''}
              ${v.heart_rate ? `HR ${esc(v.heart_rate)} ` : ''}
              ${v.temperature ? `T ${esc(v.temperature)}°F ` : ''}
              ${v.oxygen_saturation ? `SpO₂ ${esc(v.oxygen_saturation)}% ` : ''}
              ${v.weight ? `Wt ${esc(v.weight)} lb` : ''}
            </td>
            <td>
              ${v.iv_solution ? `<div><b>Solution:</b> ${esc(v.iv_solution)}${v.additives ? ' + ' + esc(v.additives) : ''}</div>` : ''}
              ${v.chief_complaint ? `<div><b>CC:</b> ${esc(v.chief_complaint)}</div>` : ''}
              ${v.assessment ? `<div><b>Assessment:</b> ${esc(v.assessment)}</div>` : ''}
              ${v.treatment_notes ? `<div><b>Notes:</b> ${esc(v.treatment_notes)}</div>` : ''}
              ${v.adverse_reactions ? `<div class="warn"><b>⚠ Adverse:</b> ${esc(v.adverse_reactions)}</div>` : ''}
            </td>
          </tr>`).join('');

    const apptRows = appts.length === 0
      ? '<tr><td colspan="4" class="empty">No appointments</td></tr>'
      : appts.slice(0, 20).map(a => {
          const dt = a.start_datetime ? format(new Date(a.start_datetime), 'EEE MMM d yyyy, h:mm a') : (a.start_datetime || '');
          const staff = (a.staff || []).map(s => `${s.first_name} ${s.last_name}`).join(', ');
          return `<tr>
            <td>${esc(dt)}</td>
            <td>${safe(a.service_type?.name)}</td>
            <td>${esc(staff || '—')}</td>
            <td>${esc((a.status || 'scheduled').replace('_', ' '))}${a.total_amount ? ` · $${a.total_amount.toFixed(2)}` : ''}</td>
          </tr>`;
        }).join('');

    const noteRows = cnotes.length === 0
      ? '<tr><td colspan="4" class="empty">No chart notes</td></tr>'
      : cnotes.map(n => `
          <tr>
            <td>${n.created_at ? esc(format(parseISO(n.created_at), 'MMM d yyyy h:mm a')) : ''}</td>
            <td>${esc(n.category || 'general')}</td>
            <td>${safe(n.author_name)}</td>
            <td>${esc(n.body || '')}</td>
          </tr>`).join('');

    const fupRows = fups.length === 0
      ? '<tr><td colspan="3" class="empty">No follow-ups</td></tr>'
      : fups.map(f => `<tr><td>${esc(f.due_date || '')}</td><td>${safe(f.reason)}</td><td>${esc(f.status || '')}</td></tr>`).join('');

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Patient Chart – ${esc(p.first_name)} ${esc(p.last_name)}</title>
<style>
  @page { size: letter; margin: 0.5in; }
  * { box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #111; font-size: 11pt; margin: 0; padding: 0; }
  .header { border-bottom: 3px solid #b8860b; padding-bottom: 12px; margin-bottom: 18px; display: flex; justify-content: space-between; align-items: flex-start; }
  .brand { font-size: 22pt; font-weight: 700; letter-spacing: 1px; }
  .brand .gold { color: #b8860b; }
  .brand-sub { font-size: 8pt; letter-spacing: 3px; color: #666; text-transform: uppercase; margin-top: 2px; }
  .printed { font-size: 9pt; color: #555; text-align: right; }
  h1 { font-size: 16pt; margin: 0 0 4px 0; color: #222; }
  .sub { color: #555; font-size: 10pt; }
  .alert { background: #fff3f3; border: 1px solid #c0392b; color: #c0392b; padding: 8px 12px; border-radius: 4px; margin: 10px 0; font-weight: 600; }
  h2 { font-size: 11pt; text-transform: uppercase; letter-spacing: 1.5px; color: #b8860b; margin: 18px 0 6px 0; border-bottom: 1px solid #e2c275; padding-bottom: 3px; }
  .grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px 18px; margin-bottom: 8px; }
  .field { font-size: 10pt; }
  .field .label { color: #666; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.5px; }
  .field .val { color: #111; }
  table { width: 100%; border-collapse: collapse; margin-top: 4px; font-size: 9.5pt; }
  th, td { text-align: left; padding: 6px 8px; vertical-align: top; border-bottom: 1px solid #eee; }
  th { background: #faf3df; color: #5a4400; font-weight: 600; font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.5px; }
  td .warn { color: #c0392b; }
  .empty { color: #999; text-align: center; font-style: italic; padding: 14px; }
  .footer { margin-top: 24px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 8pt; color: #777; text-align: center; }
  .confidential { font-size: 8pt; color: #888; margin-top: 2px; }
  .consent { display: flex; gap: 18px; margin-top: 6px; font-size: 10pt; }
  .consent .item { padding: 4px 0; }
  .yes { color: #27713a; font-weight: 600; }
  .no { color: #c0392b; font-weight: 600; }
  @media print { .no-print { display: none; } }
</style></head><body>
<div class="header">
  <div>
    <div class="brand"><span class="gold">Elite</span><span style="color:#777">IV</span></div>
    <div class="brand-sub">EMR · Patient Chart</div>
  </div>
  <div class="printed">
    Printed: ${esc(today)}<br/>
    By: Authorized Staff
  </div>
</div>

<h1>${esc(p.first_name)} ${esc(p.last_name)}</h1>
<div class="sub">
  ${age != null ? age + ' yrs · ' : ''}${p.date_of_birth ? 'DOB ' + esc(p.date_of_birth) + ' · ' : ''}${p.gender ? esc(p.gender) + ' · ' : ''}${p.phone ? esc(formatPhone(p.phone)) + ' · ' : ''}${p.email ? esc(p.email) : ''}
</div>

${p.allergies ? `<div class="alert">⚠ ALLERGIES: ${esc(p.allergies)}</div>` : ''}

<h2>Demographics & Contact</h2>
<div class="grid">
  <div class="field"><div class="label">Address</div><div class="val">${safe([p.address, p.city, p.state, p.zip_code].filter(Boolean).join(', '))}</div></div>
  <div class="field"><div class="label">Emergency Contact</div><div class="val">${safe(p.emergency_contact_name)}${p.emergency_contact_relationship ? ' (' + esc(p.emergency_contact_relationship) + ')' : ''}</div></div>
  <div class="field"><div class="label">Emergency Phone</div><div class="val">${safe(formatPhone(p.emergency_contact_phone))}</div></div>
</div>

<h2>Medical History</h2>
<div class="grid">
  <div class="field"><div class="label">Current Medications</div><div class="val">${safe(p.current_medications)}</div></div>
  <div class="field"><div class="label">Medical Conditions</div><div class="val">${safe(p.medical_conditions)}</div></div>
  <div class="field"><div class="label">Past Surgeries</div><div class="val">${safe(p.past_surgeries)}</div></div>
  <div class="field"><div class="label">Prior IV Therapy</div><div class="val">${p.prior_iv_therapy ? 'Yes' : 'No'}${p.prior_iv_notes ? ' – ' + esc(p.prior_iv_notes) : ''}</div></div>
  <div class="field"><div class="label">Hydration Goals</div><div class="val">${safe(p.hydration_concerns)}</div></div>
  <div class="field"><div class="label">Intake Notes</div><div class="val">${safe(p.notes)}</div></div>
</div>

<h2>Consent</h2>
<div class="consent">
  <div class="item">HIPAA: <span class="${p.hipaa_acknowledged ? 'yes' : 'no'}">${p.hipaa_acknowledged ? '✓ Acknowledged' : '✗ Not signed'}</span></div>
  <div class="item">Treatment: <span class="${p.treatment_consent ? 'yes' : 'no'}">${p.treatment_consent ? '✓ Consented' : '✗ Not signed'}</span></div>
  ${p.consent_signed_at ? `<div class="item">Signed: ${esc(format(parseISO(p.consent_signed_at), 'MMM d, yyyy'))}</div>` : ''}
</div>

<h2>Visits (${visits.length})</h2>
<table>
  <thead><tr><th style="width:90px">Date</th><th style="width:130px">Service</th><th style="width:200px">Vitals</th><th>Clinical</th></tr></thead>
  <tbody>${visitRows}</tbody>
</table>

<h2>Appointments (${appts.length})</h2>
<table>
  <thead><tr><th>Date / Time</th><th>Service</th><th>Staff</th><th>Status / Amount</th></tr></thead>
  <tbody>${apptRows}</tbody>
</table>

<h2>Follow-Ups (${fups.length})</h2>
<table>
  <thead><tr><th style="width:120px">Due</th><th>Reason</th><th style="width:110px">Status</th></tr></thead>
  <tbody>${fupRows}</tbody>
</table>

<h2>Chart Notes (${cnotes.length})</h2>
<table>
  <thead><tr><th style="width:150px">Created</th><th style="width:120px">Category</th><th style="width:140px">Author</th><th>Note</th></tr></thead>
  <tbody>${noteRows}</tbody>
</table>

<div class="footer">
  <div><b>CONFIDENTIAL – PROTECTED HEALTH INFORMATION</b></div>
  <div class="confidential">This document contains information protected under HIPAA. Unauthorized disclosure is prohibited. EliteIV Lounge.</div>
</div>

<script>window.onload = function(){ setTimeout(function(){ window.print(); }, 350); };</script>
</body></html>`;

    const w = window.open('', '_blank', 'width=900,height=1200');
    if (!w) { toast.error('Pop-up blocked. Please allow pop-ups to print.'); return; }
    logClientEvent('Printed patient chart', { patient_id: p.id, resource_type: 'patient', resource_id: p.id });
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  if (!patient) return null;

  const p = chart?.patient || patient;
  const age = calcAge(p.date_of_birth);
  const visits = chart?.visits || [];
  const appointments = chart?.appointments || [];
  const followUps = chart?.follow_ups || [];
  const notes = chart?.chart_notes || [];
  const stats = chart?.stats || {};

  const categoryColor = (c) => ({
    clinical: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    billing: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    communication: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
    general: 'bg-muted/40 text-muted-foreground border-border/40',
  }[c] || 'bg-muted/40 text-muted-foreground border-border/40');

  return (
    <Dialog open={!!patient} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-0 bg-card gold-border" data-testid="patient-chart-modal">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-black via-zinc-950 to-black border-b border-primary/30 px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 ring-2 ring-primary/50">
                <AvatarFallback className="bg-primary/15 text-primary text-lg font-semibold">{getInitials(p.first_name, p.last_name)}</AvatarFallback>
              </Avatar>
              <div>
                <DialogTitle className="text-2xl brand-heading">
                  <span className="gold-gradient-text">{p.first_name} {p.last_name}</span>
                </DialogTitle>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mt-1">
                  {age !== null && <span>{age} yrs</span>}
                  {p.date_of_birth && <span>· DOB {p.date_of_birth}</span>}
                  {p.gender && <span>· {p.gender}</span>}
                  {p.phone && <span>· {formatPhone(p.phone)}</span>}
                  {p.email && <span>· {p.email}</span>}
                </div>
                {p.allergies && (
                  <Badge variant="outline" className="mt-2 border-destructive/50 text-destructive bg-destructive/10" data-testid="patient-allergies-banner">
                    ⚠ Allergies: {p.allergies}
                  </Badge>
                )}
                {p.intake_completed_at ? (
                  <Badge variant="outline" className="mt-2 ml-2 border-emerald-500/50 text-emerald-300 bg-emerald-500/10" data-testid="chart-intake-done">
                    ✓ Intake completed {p.intake_completed_at ? `· ${format(parseISO(p.intake_completed_at), 'MMM d')}` : ''}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="mt-2 ml-2 border-amber-500/40 text-amber-300/80 bg-amber-500/5">
                    ⏳ Intake pending
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={async () => {
                if (!p?.email) { toast.error('Patient has no email on file'); return; }
                try {
                  await axios.post(`${API}/patients/${p.id}/send-intake`);
                  toast.success('Intake form emailed to patient');
                } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Failed'); }
              }} data-testid="chart-send-intake" className="border-primary/40 hover:bg-primary/10"><Send className="w-4 h-4 mr-1" />Send Intake Form</Button>
              <Button size="sm" variant="outline" onClick={printChart} disabled={!chart || loading} data-testid="chart-print-btn" className="border-primary/40 hover:bg-primary/10"><Printer className="w-4 h-4 mr-1" />Print Chart</Button>
              <Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid="chart-edit-patient"><Edit className="w-4 h-4 mr-1" />Edit Demographics</Button>
              <Button size="icon" variant="ghost" onClick={onClose} data-testid="chart-close"><X className="w-4 h-4" /></Button>
            </div>
          </div>

          {/* Stat strip */}
          <div className="grid grid-cols-4 gap-3 mt-4">
            <div className="bg-muted/30 rounded-lg p-2 border border-border/40">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Visits</p>
              <p className="text-xl font-bold text-primary">{stats.total_visits ?? '—'}</p>
            </div>
            <div className="bg-muted/30 rounded-lg p-2 border border-border/40">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Appointments</p>
              <p className="text-xl font-bold text-primary">{stats.total_appointments ?? '—'}</p>
            </div>
            <div className="bg-muted/30 rounded-lg p-2 border border-border/40">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Pending Follow-Ups</p>
              <p className="text-xl font-bold text-primary">{stats.pending_followups ?? '—'}</p>
            </div>
            <div className="bg-muted/30 rounded-lg p-2 border border-border/40">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Chart Notes</p>
              <p className="text-xl font-bold text-primary">{stats.total_notes ?? '—'}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pb-6 pt-4">
          <Tabs value={tab} onValueChange={(t) => { setTab(t); if (t !== 'overview') logClientEvent(`Viewed chart tab: ${t}`, { patient_id: p.id, resource_type: 'patient', resource_id: p.id }); }} className="w-full">
            <TabsList className="w-full justify-start flex-wrap h-auto bg-muted/30">
              <TabsTrigger value="overview" data-testid="chart-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="medical" data-testid="chart-tab-medical">Medical</TabsTrigger>
              <TabsTrigger value="visits" data-testid="chart-tab-visits">Visits ({visits.length})</TabsTrigger>
              <TabsTrigger value="appointments" data-testid="chart-tab-appointments">Appointments ({appointments.length})</TabsTrigger>
              <TabsTrigger value="followups" data-testid="chart-tab-followups">Follow-Ups ({followUps.length})</TabsTrigger>
              <TabsTrigger value="notes" data-testid="chart-tab-notes">Chart Notes ({notes.length})</TabsTrigger>
              <TabsTrigger value="consent" data-testid="chart-tab-consent">Consent</TabsTrigger>
              <TabsTrigger value="documents" data-testid="chart-tab-documents">Documents</TabsTrigger>
              <TabsTrigger value="progressnotes" data-testid="chart-tab-progressnotes">Progress Notes</TabsTrigger>
              <TabsTrigger value="membership" data-testid="chart-tab-membership" className="gap-1"><Crown className="w-3.5 h-3.5" />Membership</TabsTrigger>
            </TabsList>

            {loading && <p className="text-center text-muted-foreground py-8">Loading chart…</p>}

            {/* OVERVIEW */}
            <TabsContent value="overview" className="mt-4 space-y-5">
              <Card className="bg-muted/20 border-border/40">
                <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wider text-primary">Demographics</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <InfoField label="First Name" value={p.first_name} />
                  <InfoField label="Last Name" value={p.last_name} />
                  <InfoField label="Date of Birth" value={p.date_of_birth} />
                  <InfoField label="Age" value={age != null ? `${age} years` : null} />
                  <InfoField label="Gender" value={p.gender} />
                  <InfoField label="Phone" value={formatPhone(p.phone)} />
                  <InfoField label="Email" value={p.email} />
                  <InfoField label="Address" value={p.address} />
                  <InfoField label="City / State / Zip" value={[p.city, p.state, p.zip_code].filter(Boolean).join(', ')} />
                </CardContent>
              </Card>

              <Card className="bg-muted/20 border-border/40">
                <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wider text-primary">Emergency Contact</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <InfoField label="Name" value={p.emergency_contact_name} />
                  <InfoField label="Phone" value={formatPhone(p.emergency_contact_phone)} />
                  <InfoField label="Relationship" value={p.emergency_contact_relationship} />
                </CardContent>
              </Card>

              {p.notes && (
                <Card className="bg-muted/20 border-border/40">
                  <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wider text-primary">Intake Notes</CardTitle></CardHeader>
                  <CardContent><p className="text-sm whitespace-pre-wrap">{p.notes}</p></CardContent>
                </Card>
              )}
            </TabsContent>

            {/* MEDICAL */}
            <TabsContent value="medical" className="mt-4 space-y-5">
              <Card className="bg-destructive/5 border-destructive/30">
                <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wider text-destructive">Allergies</CardTitle></CardHeader>
                <CardContent><p className="text-sm whitespace-pre-wrap">{p.allergies || <span className="text-muted-foreground">None reported</span>}</p></CardContent>
              </Card>

              <div className="grid md:grid-cols-2 gap-4">
                <Card className="bg-muted/20 border-border/40">
                  <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wider text-primary">Current Medications</CardTitle></CardHeader>
                  <CardContent><p className="text-sm whitespace-pre-wrap">{p.current_medications || <span className="text-muted-foreground">None reported</span>}</p></CardContent>
                </Card>
                <Card className="bg-muted/20 border-border/40">
                  <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wider text-primary">Medical Conditions</CardTitle></CardHeader>
                  <CardContent><p className="text-sm whitespace-pre-wrap">{p.medical_conditions || <span className="text-muted-foreground">None reported</span>}</p></CardContent>
                </Card>
                <Card className="bg-muted/20 border-border/40">
                  <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wider text-primary">Past Surgeries</CardTitle></CardHeader>
                  <CardContent><p className="text-sm whitespace-pre-wrap">{p.past_surgeries || <span className="text-muted-foreground">None reported</span>}</p></CardContent>
                </Card>
                <Card className="bg-muted/20 border-border/40">
                  <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wider text-primary">IV Therapy History</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-sm"><span className="text-muted-foreground">Prior IV therapy: </span>{p.prior_iv_therapy ? 'Yes' : 'No'}</p>
                    {p.prior_iv_notes && <p className="text-sm whitespace-pre-wrap">{p.prior_iv_notes}</p>}
                    {p.hydration_concerns && <p className="text-sm"><span className="text-muted-foreground">Goals: </span>{p.hydration_concerns}</p>}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* VISITS */}
            <TabsContent value="visits" className="mt-4 space-y-3">
              {visits.length === 0 && <p className="text-center text-muted-foreground py-8">No documented visits yet.</p>}
              {visits.map(v => (
                <Card key={v.id} className="bg-muted/20 border-border/40" data-testid={`chart-visit-${v.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <CardTitle className="text-sm">
                        <span className="text-primary">{v.visit_date}</span>
                        {v.service_type?.name && <span className="text-muted-foreground"> · {v.service_type.name}</span>}
                      </CardTitle>
                      {v.staff && <Badge variant="outline" className="text-xs">{v.staff.first_name} {v.staff.last_name}</Badge>}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    {(v.blood_pressure || v.heart_rate || v.temperature || v.oxygen_saturation || v.weight) && (
                      <div className="flex flex-wrap gap-3 text-xs">
                        {v.blood_pressure && <span className="bg-muted/40 px-2 py-1 rounded">BP {v.blood_pressure}</span>}
                        {v.heart_rate && <span className="bg-muted/40 px-2 py-1 rounded">HR {v.heart_rate}</span>}
                        {v.temperature && <span className="bg-muted/40 px-2 py-1 rounded">Temp {v.temperature}°F</span>}
                        {v.oxygen_saturation && <span className="bg-muted/40 px-2 py-1 rounded">SpO₂ {v.oxygen_saturation}%</span>}
                        {v.weight && <span className="bg-muted/40 px-2 py-1 rounded">Wt {v.weight} lb</span>}
                      </div>
                    )}
                    {v.iv_solution && <p><span className="text-muted-foreground">Solution: </span>{v.iv_solution}{v.additives ? ` + ${v.additives}` : ''}</p>}
                    {v.chief_complaint && <p><span className="text-muted-foreground">Chief complaint: </span>{v.chief_complaint}</p>}
                    {v.assessment && <p><span className="text-muted-foreground">Assessment: </span>{v.assessment}</p>}
                    {v.treatment_notes && <p className="whitespace-pre-wrap"><span className="text-muted-foreground">Notes: </span>{v.treatment_notes}</p>}
                    {v.adverse_reactions && <p className="text-destructive">⚠ Adverse: {v.adverse_reactions}</p>}
                    {v.post_treatment_instructions && <p><span className="text-muted-foreground">Post-treatment: </span>{v.post_treatment_instructions}</p>}
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            {/* APPOINTMENTS */}
            <TabsContent value="appointments" className="mt-4 space-y-2">
              {appointments.length === 0 && <p className="text-center text-muted-foreground py-8">No appointments on file.</p>}
              {appointments.map(a => {
                const dt = a.start_datetime ? new Date(a.start_datetime) : null;
                const statusColor = {
                  scheduled: 'bg-sky-500/15 text-sky-300',
                  checked_in: 'bg-amber-500/15 text-amber-300',
                  completed: 'bg-emerald-500/15 text-emerald-300',
                  cancelled: 'bg-destructive/15 text-destructive',
                  no_show: 'bg-destructive/15 text-destructive',
                }[a.status] || 'bg-muted/40 text-muted-foreground';
                return (
                  <div key={a.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg border border-border/40" data-testid={`chart-appt-${a.id}`}>
                    <div>
                      <p className="text-sm font-medium">
                        {dt ? format(dt, 'EEE, MMM d yyyy · h:mm a') : a.start_datetime}
                        {a.service_type?.name && <span className="text-muted-foreground"> · {a.service_type.name}</span>}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {a.staff?.length ? a.staff.map(s => `${s.first_name} ${s.last_name}`).join(', ') : 'No staff assigned'}
                        {a.checked_out_at && a.total_amount ? ` · $${a.total_amount.toFixed(2)} ${a.payment_status === 'unpaid' ? '(unpaid)' : '(paid)'}` : ''}
                      </p>
                      {a.service_address && (
                        <p className="text-xs mt-1">
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.service_address)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:text-amber-300 underline underline-offset-2"
                          >📍 {a.service_address}</a>
                          {a.parking_instructions && <span className="text-muted-foreground"> · {a.parking_instructions}</span>}
                        </p>
                      )}
                      {a.notes && <p className="text-xs text-muted-foreground mt-1 italic">"{a.notes}"</p>}
                    </div>
                    <Badge variant="outline" className={`${statusColor} border-0`}>{(a.status || 'scheduled').replace('_', ' ')}</Badge>
                  </div>
                );
              })}
            </TabsContent>

            {/* FOLLOW-UPS */}
            <TabsContent value="followups" className="mt-4 space-y-2">
              {followUps.length === 0 && <p className="text-center text-muted-foreground py-8">No follow-ups recorded.</p>}
              {followUps.map(f => (
                <div key={f.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg border border-border/40">
                  <div>
                    <p className="text-sm font-medium">Due {f.due_date} {f.reason ? `· ${f.reason}` : ''}</p>
                    {f.notes && <p className="text-xs text-muted-foreground mt-1">{f.notes}</p>}
                  </div>
                  <Badge variant="outline" className={f.status === 'completed' ? 'bg-emerald-500/15 text-emerald-300 border-0' : 'bg-amber-500/15 text-amber-300 border-0'}>
                    {f.status}
                  </Badge>
                </div>
              ))}
            </TabsContent>

            {/* CHART NOTES */}
            <TabsContent value="notes" className="mt-4 space-y-4">
              <Card className="bg-primary/5 border-primary/30">
                <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wider text-primary">Add Chart Note</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex gap-2">
                    <Select value={noteCategory} onValueChange={setNoteCategory}>
                      <SelectTrigger className="w-40" data-testid="chart-note-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="clinical">Clinical</SelectItem>
                        <SelectItem value="billing">Billing</SelectItem>
                        <SelectItem value="communication">Communication</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea
                    rows={3}
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a chart note for this patient (visible to all staff)..."
                    data-testid="chart-note-input"
                  />
                  <div className="flex justify-end">
                    <Button size="sm" onClick={addNote} disabled={savingNote || !newNote.trim()} className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold" data-testid="chart-note-save">
                      <Plus className="w-4 h-4 mr-1" />{savingNote ? 'Saving…' : 'Save Note'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-2" data-testid="chart-notes-list">
                {notes.length === 0 && <p className="text-center text-muted-foreground py-8">No chart notes yet. Add the first note above.</p>}
                {notes.map(n => (
                  <div key={n.id} className="p-3 bg-muted/20 rounded-lg border border-border/40" data-testid={`chart-note-${n.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge variant="outline" className={`text-[10px] ${categoryColor(n.category)}`}>{n.category || 'general'}</Badge>
                          <span className="text-xs text-muted-foreground">{n.author_name || 'Unknown'} · {n.created_at ? format(parseISO(n.created_at), 'MMM d yyyy h:mm a') : ''}</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{n.body}</p>
                      </div>
                      <Button variant="ghost" size="icon" className="text-destructive h-7 w-7" onClick={() => deleteNote(n.id)} data-testid={`chart-note-delete-${n.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* CONSENT */}
            <TabsContent value="consent" className="mt-4">
              <ConsentTab patient={p} onChanged={load} />
            </TabsContent>

            {/* DOCUMENTS */}
            <TabsContent value="documents" className="mt-4">
              <DocumentsTab patient={p} />
            </TabsContent>

            {/* PROGRESS NOTES */}
            <TabsContent value="progressnotes" className="mt-4">
              <PatientProgressNotesTab patientId={p.id} patient={p} />
            </TabsContent>
            <TabsContent value="membership" className="mt-4">
              <MembershipTab patient={p} canManage={user?.role !== 'management'} />
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};
